const wsMap: Map<string, WsServiceModule> = new Map();
const channelConnScheme = document.location.protocol === 'http:' ? 'ws' : 'wss';

/** Websocket codes according to RFC6455 */
const WEBSOCKET_CODES = {
	NORMAL_CLOSURE: 1000,
	GOING_AWAY: 1001, // e.g. page refresh/navigation
	PROTOCOL_ERROR: 1002,
	UNSUPPORTED_DATA: 1003,
	// RESERVED: 1004, // reserved in RFC
	NO_STATUS_RCVD: 1005,
	ABNORMAL_CLOSURE: 1006, // Most common error when websockets not available
	INVALID_FRAME_PAYLOAD_DATA: 1007,
	POLICY_VIOLATION: 1008,
	MESSAGE_TOO_BIG: 1009,
	MANDATORY_EXT: 1010,
	INTERNAL_ERROR: 1011,
	TLS_HANDSHAKE: 1015
};

export class CustomEventListener {
	protected cbs: Array<Common.FnAny> = [];

	public addEventListenerIfNew(cb: Common.FnAny): void {
		if (!this.existsEventListener(cb)) {
			this.cbs.push(cb);
		}
	}

	public removeEventListener(cb: Common.FnAny): void {
		const idx = this.cbs.findIndex((func) => cb === func);
		idx > -1 && this.cbs.splice(idx, 1);
	}

	public call(event: Event): void {
		this.cbs.forEach((cb) => {
			try {
				cb(event);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn('CustomEventListener error: ', err);
			}
		});
	}

	protected existsEventListener(cb: Common.FnAny): boolean {
		return this.cbs.findIndex((func) => cb === func) > -1;
	}
}

export default class WsServiceModule {
	public static getOrCreateWS(url: string): WsServiceModule {
		return wsMap.get(url) || new WsServiceModule(url);
	}

	private ws!: WebSocket;
	private connectPromise!: Promise<void>;
	private readonly url: string;
	private readonly wsURL: string;

	public onOpen = new CustomEventListener();
	public onError = new CustomEventListener();
	public onClose = new CustomEventListener();
	public onMessage = new CustomEventListener();

	constructor(url: string) {
		this.url = url;
		this.wsURL = `${channelConnScheme}://${document.location.host}/${url}`;
		wsMap.set(url, this);
	}

	public get connected(): boolean {
		return this.ws && this.ws.readyState === this.ws.OPEN;
	}

	public start(): Promise<void> {
		this.connectPromise = this.openWS();
		return this.connectPromise;
	}

	public async send(command: object = {}): Promise<void> {
		await this.connectPromise;
		this.ws.send(JSON.stringify(command));
	}

	/** Force closing current ws connection (if possible) */
	public forceClose(code?: number, reason?: string): void {
		this.ws?.close(code, reason);
	}

	private openWS(): Promise<void> {
		return new Promise((resolve) => {
			this.ws = new WebSocket(this.wsURL);

			this.ws.onopen = (event): void => {
				// eslint-disable-next-line no-console
				console.log(`UX1: WS channel '${this.wsURL}' opened.`, event);
				this.onOpen.call(event);
				resolve();
			};

			this.ws.onmessage = (event): void => this.onMessage.call(event);
			this.ws.onerror = (event): void => this.onErrorHandler(event);
			this.ws.onclose = (event): void => this.onCloseHandler(event);
		});
	}

	private onErrorHandler(event: Event): void {
		// todo add to logger
		// eslint-disable-next-line no-console
		console.log(`UX1: WS channel '${this.wsURL}' error!`, event);

		this.onError.call(event);
	}

	private onCloseHandler(event: CloseEvent): void {
		// We need to redefine wasClean as it's working differently in different browsers.
		// E.g. Chrome not closing channel and throwing non clean closure on page refresh
		// while Firefox handling gracefully and showing "normal" closure in the same conditions.
		// We need consistent behavior.
		const fixedEvent = new CloseEvent('close', {
			reason: event.reason,
			code: event.code,
			wasClean: event.code === WEBSOCKET_CODES.NORMAL_CLOSURE || event.code === WEBSOCKET_CODES.GOING_AWAY
		});

		// eslint-disable-next-line no-console
		console.log(`UX1: WS channel '${this.wsURL}' closed: `, fixedEvent);

		wsMap.delete(this.url);

		this.onClose.call(fixedEvent);
	}
}
